import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useUserSettings } from '~/components/UserSettings';
import { useCompanyPreferences } from '~/data/companyPreferences/useCompanyPreferences';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { useWorkspaceInitializationContext } from '~/scenes/MyWorkspace/context';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import {
  CompanyWorkspaceSettingsKey,
  MyWorkspaceUserSettingsKey,
  WorkspaceId_NO_WORKSPACE,
  WorkspaceSettings,
} from '~/scenes/MyWorkspace/userSettings';

import {
  filterAdminOnlyWorkspaces,
  filterUniqueWorkspaces,
  getGeneralWorkspacesSettingsResolver,
} from './data';

export interface WorkspacesSettings {
  workspaces: Array<WorkspaceSettings>;
  activeWorkspaceId?: string;
  useWorkspaceAsHomepage?: boolean;
}

export interface UseAllWorkspacesOutput {
  workspaces: Array<WorkspaceSettings>;
  activeWorkspace: WorkspaceSettings;
  adminOnlyWorkspaces: Array<WorkspaceSettings>;
  activeWorkspaceRole: AdminWorkspaceRoles;
  hasData: boolean;
}

export const useWorkspaceSettings = (): UseAllWorkspacesOutput => {
  const { t } = useTranslation();

  const { activeWorkspaceId } = useWorkspaceInitializationContext();

  const userRoles = useLoggedInUserRoles();
  const workspaceRoles = useMemo((): AdminWorkspaceRoles[] => {
    const userWorkspaceRoles = Object.values(AdminWorkspaceRoles).filter((r) =>
      userRoles.includes(r),
    );
    return userWorkspaceRoles.includes(AdminWorkspaceRoles.EMPLOYEE)
      ? userWorkspaceRoles
      : [...userWorkspaceRoles, AdminWorkspaceRoles.EMPLOYEE];
  }, [userRoles]);

  // Company Workspaces
  const resolveCompanyWorkspaceSettings = useMemo(() => {
    return getGeneralWorkspacesSettingsResolver({
      roles: workspaceRoles,
      workspacesType: WorkspaceType.Company,
    });
  }, [workspaceRoles]);

  const { companyWorkspaces, hasDataCompanyWorkspaces } = useCompanyPreferences(
    resolveCompanyWorkspaceSettings,
    CompanyWorkspaceSettingsKey,
  );

  // Personal Workspaces
  const resolveMyWorkspaceUserSettings = useMemo(() => {
    return getGeneralWorkspacesSettingsResolver({
      roles: workspaceRoles,
      workspacesType: WorkspaceType.Personal,
    });
  }, [workspaceRoles]);

  const { personalWorkspaces, hasDataPersonalWorkspaces } = useUserSettings(
    resolveMyWorkspaceUserSettings,
    MyWorkspaceUserSettingsKey,
  );

  // Combined workspaces
  const workspaces: Array<WorkspaceSettings> = useMemo(() => {
    return filterUniqueWorkspaces([
      ...companyWorkspaces.workspaces,
      ...personalWorkspaces.workspaces,
    ]);
  }, [companyWorkspaces, personalWorkspaces]);

  const activeWorkspace: WorkspaceSettings = useMemo(() => {
    if (workspaces.length <= 0) {
      return {
        id: WorkspaceId_NO_WORKSPACE,
        name: t('createWorkspace'),
        widgets: [],
        type: WorkspaceType.Default,
        restrictToRoles: [],
      };
    }

    return (
      workspaces.find(({ id }) => {
        return id === activeWorkspaceId;
      }) || workspaces[0]
    );
  }, [t, workspaces, activeWorkspaceId]);

  const adminOnlyWorkspaces: WorkspaceSettings[] = useMemo(() => {
    return filterAdminOnlyWorkspaces(
      companyWorkspaces.workspaces,
      workspaceRoles,
    );
  }, [companyWorkspaces, workspaceRoles]);

  return {
    workspaces,
    activeWorkspace,
    adminOnlyWorkspaces,
    activeWorkspaceRole: activeWorkspace?.restrictToRoles?.[0],
    hasData: hasDataCompanyWorkspaces && hasDataPersonalWorkspaces,
  };
};
